import { useQuery } from 'react-query'

import { usePagination } from '@webapp/core/hooks/usePagination'
import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { type ApprovedExpenseItemListParams } from '@webapp/modules/expense/data-access/types/ApprovedExpenseItemListParams'
import {
  ApprovedExpenseItem,
  type ApprovedExpenseItemConstructor,
} from '@webapp/modules/expense/entities/ApprovedExpenseItem'
import { expenseQueryConfig } from '@webapp/modules/expense/query-configs/expense'

type UseQueryApprovedExpenseItemsReturn = {
  items: ApprovedExpenseItem[]
  pagination?: RepoPaginationResponse
  isFetching: boolean
  error: Nullable<RepoError>
}

export const useQueryApprovedExpenseItems = (
  params?: ApprovedExpenseItemListParams
): UseQueryApprovedExpenseItemsReturn => {
  const { params: paginationParams, updateNumPages } = usePagination({
    id: 'approved-expense-items',
  })

  const { data, isFetching, error } = useQuery<
    RepoPaginatedResponse<ApprovedExpenseItemConstructor>,
    RepoError
  >({
    ...expenseQueryConfig.listApprovedItems({
      ...paginationParams,
      ...params,
    }),
    onSuccess: (res) => {
      updateNumPages(res.pagination.numPages)
    },
    keepPreviousData: true,
  })

  return {
    items: (data?.data ?? []).map(
      (constructor) => new ApprovedExpenseItem(constructor)
    ),
    pagination: data?.pagination,
    isFetching,
    error: error ?? null,
  }
}
